import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircleOutline,
  Restaurant,
  Dashboard,
  MenuBook,
  Settings,
} from '@mui/icons-material';

const BrandClaimSuccess = () => {
  const location = useLocation();
  const history = useHistory();
  const { brand, dashboardUrl } = location.state || {};
  const [secondsLeft, setSecondsLeft] = useState(40);

  useEffect(() => {
    document.title = 'Brand Claimed Successfully | Piatto';
  }, []);

  useEffect(() => {
    const countdown = setInterval(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);

    const redirect = setTimeout(() => {
      clearInterval(countdown);
      history.push('/Home');
    }, 40000);

    return () => {
      clearInterval(countdown);
      clearTimeout(redirect);
    };
  }, [history]);

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircleOutline
          sx={{ fontSize: 100, color: 'success.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Congratulations!{' '}
          <span aria-hidden="true">🎉</span>
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          You've successfully claimed
          {brand?.venueCount ? ` all ${brand.venueCount}` : ''}{' '}
          {brand?.displayName || 'brand'} venues on Piatto
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {brand?.displayName || 'Your Brand'}
            </Typography>
            <Typography color="text.secondary">
              {brand?.venueCount ? `${brand.venueCount} venues` : ''}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            What's Next?
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Dashboard sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Browse Your Venues
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View all your locations from a single dashboard with search and filtering
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MenuBook sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Manage Menus
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and update menus for each location, add new dishes, set prices
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Settings sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Configure Accessibility Features
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Showcase each venue's accessibility features (ramps, parking, bathrooms)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Restaurant sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Accept Pre-Arranged Orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start receiving orders from customers with accessibility requirements
                </Typography>
              </Box>
            </Box>
          </Stack>

          <Box sx={{ mt: 3, p: 3, borderRadius: 2, bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Connect your POS for the full Piatto experience
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When you connect your point-of-sale system (like Square), Piatto can import
              your menu automatically and present it to diners &mdash; including
              screen-reader users &mdash; in a fully accessible way on the Piatto app. Each
              order comes through to your POS complete with the customer's accessibility and
              allergen needs. Look for the POS connection option in each venue's dashboard.
            </Typography>
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => history.push('/Home')}
              startIcon={<Dashboard aria-hidden="true" />}
              sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 600 }}
            >
              Go to Dashboard
            </Button>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
              aria-live="polite"
              aria-atomic="true"
            >
              Redirecting automatically in {secondsLeft} second{secondsLeft !== 1 ? 's' : ''}...
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help getting started?{' '}
          <a
            href="https://piatto.com.au"
            style={{ color: '#B91C1C' }}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit piatto.com.au for help (opens in new tab)"
          >
            Visit piatto.com.au
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default BrandClaimSuccess;
